import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    Hammer,
    HeartHandshake,
    Clock,
    ShieldCheck,
    Award,
    BadgeCheck,
    Star,
    Search,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { PAGE_META, EXTERNAL_PROFILES } from "@/lib/seo-constants";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";
import { PROJECT_PHOTOS } from "@/data/photos";
import ResponsiveImg from "@/components/ResponsiveImg";

// Per-track hero + meta key. Company story, values and credentials are shared.
const ABOUT_HERO = {
    residential: {
        titleA: "Built by homeowners,",
        titleB: "for homeowners.",
        sub: "A North York-based crew serving the Greater Toronto Area — exterior and interior contracting, with one foot on every job site.",
        metaKey: "about",
    },
    commercial: {
        titleA: "One accountable vendor",
        titleB: "for your properties.",
        sub: "A North York-based contractor serving GTA commercial properties — insured, scheduled, and documented work for owners, developers and property managers.",
        metaKey: "commercialAbout",
    },
    industrial: {
        titleA: "Heavy-duty work,",
        titleB: "documented & compliant.",
        sub: "A North York-based contractor serving GTA industrial sites — durable, compliant exterior and site work, scheduled around your operations.",
        metaKey: "industrialAbout",
    },
};

const STORY_IMAGE =
    PROJECT_PHOTOS.fencing?.[0]?.src ||
    "/projects/fencing/aluminum-fence-img-20250502-wa0056.webp";

const VALUES = [
    {
        icon: Hammer,
        title: "Craftsmanship",
        body: "Every fence panel, paver line, and trim joint is checked twice — by the person who built it and by their lead.",
    },
    {
        icon: HeartHandshake,
        title: "Transparency",
        body: "Fixed line-itemed quotes. We tell you what you need — and what you don't — even when it costs us a sale.",
    },
    {
        icon: Clock,
        title: "Reliability",
        body: "We show up when we say we will, and we leave the site cleaner than we found it. Every single day.",
    },
];

// Factual credentials only — no star numbers until real review counts arrive.
const CREDENTIALS = [
    { icon: ShieldCheck, label: "WSIB Covered" },
    { icon: ShieldCheck, label: "$5M Liability Insurance" },
    { icon: ShieldCheck, label: "Fully Insured & Bonded" },
    { icon: BadgeCheck, label: "BBB Accredited A+", href: EXTERNAL_PROFILES.bbb },
    { icon: Award, label: "Homestars Verified", href: EXTERNAL_PROFILES.homestars },
    { icon: Star, label: "Houzz Pro", href: EXTERNAL_PROFILES.houzz },
    { icon: ShieldCheck, label: "Bark Certified Pro", href: EXTERNAL_PROFILES.bark },
    { icon: Search, label: "Google Reviewed", href: EXTERNAL_PROFILES.google },
];

export default function About({ trackKey = "residential" }) {
    const track = TRACKS[trackKey];
    const hero = ABOUT_HERO[trackKey] || ABOUT_HERO.residential;
    return (
        <>
            <Seo
                {...PAGE_META[hero.metaKey]}
                path={track.about}
                jsonLd={[
                    localBusinessSchema(),
                    breadcrumbListSchema([
                        ...(trackKey === "residential"
                            ? []
                            : [{ name: track.label, path: track.home }]),
                        { name: "About", path: track.about },
                    ]),
                ]}
            />

            {/* Hero */}
            <Section dark className="py-20 md:py-28" data-testid="about-hero">
                <Container>
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                            About Reformity
                        </p>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                            data-testid="about-title"
                        >
                            {hero.titleA}
                            <br />
                            <span className="text-brand-coral">{hero.titleB}</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light italic leading-relaxed">
                            {hero.sub}
                        </p>
                    </div>
                </Container>
            </Section>

            {/* Founder story */}
            <Section data-testid="about-story">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                                Our Story
                            </p>
                            <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none mb-3">
                                Shaheen Rashidi
                            </h2>
                            <p
                                className="text-xs uppercase tracking-[0.25em] text-brand-gray font-semibold mb-6"
                                data-testid="about-founder-title"
                            >
                                Founder
                            </p>
                            <p
                                className="text-base text-brand-dark/85 leading-relaxed"
                                data-testid="about-founder-story"
                            >
                                Reformity Inc. was founded in 2018 by Shaheen Rashidi,
                                a young GTA-based entrepreneur who saw an industry
                                full of older companies but short on the things
                                homeowners actually want: transparency, accountability,
                                and modern tools. Shaheen built Reformity to be
                                different — same trades, sharper execution. The company
                                uses AI and modern technology throughout the workflow,
                                from faster, more accurate quotes to better project
                                communication and tighter quality control. Every job
                                runs through a clear, documented process so customers
                                know exactly what they're paying for and what to expect.
                                It's contracting without the surprises, built by a
                                founder who treats every project like it's his own home.
                            </p>
                        </div>
                        <div className="lg:col-span-5 order-1 lg:order-2">
                            <div className="relative aspect-[4/5] overflow-hidden bg-brand-dark">
                                <ResponsiveImg
                                    src={STORY_IMAGE}
                                    alt="A recent Reformity Inc. project in the GTA"
                                    className="h-full w-full object-cover"
                                    decoding="async"
                                    sizes="(min-width: 1024px) 50vw, 100vw"
                                />
                                <div className="absolute bottom-4 left-4 right-4 bg-brand-coral text-white px-4 py-3 text-xs uppercase tracking-wider font-semibold">
                                    Based in North York · Serving the GTA
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* What we stand for */}
            <Section dark data-testid="about-values">
                <Container>
                    <div className="max-w-2xl mb-12 md:mb-14">
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                            What We Stand For
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                            Three things we never compromise on.
                        </h2>
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                        {VALUES.map((v) => {
                            const Icon = v.icon;
                            return (
                                <li
                                    key={v.title}
                                    className="bg-[#333] border border-white/10 p-7 md:p-8 hover:border-brand-cyan transition-colors"
                                    data-testid={`about-value-${v.title.toLowerCase()}`}
                                >
                                    <span className="flex h-12 w-12 items-center justify-center bg-brand-coral text-white mb-5">
                                        <Icon className="h-6 w-6" strokeWidth={2.25} />
                                    </span>
                                    <h3 className="font-display text-2xl tracking-wide text-white mb-2">
                                        {v.title}
                                    </h3>
                                    <p className="text-sm text-white/70 leading-relaxed">
                                        {v.body}
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                </Container>
            </Section>

            {/* Credentials & certifications */}
            <Section data-testid="about-credentials">
                <Container>
                    <div className="max-w-2xl mb-10 md:mb-14">
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                            Credentials &amp; Certifications
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                            Verified. Covered. Accountable.
                        </h2>
                    </div>
                    <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {CREDENTIALS.map((c) => {
                            const Icon = c.icon;
                            const slug = c.label
                                .toLowerCase()
                                .replace(/\s+/g, "-")
                                .replace(/[^a-z0-9-]/g, "");
                            const inner = (
                                <>
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-brand-cyan/15 text-brand-cyan">
                                        <Icon className="h-4 w-4" strokeWidth={2.25} />
                                    </span>
                                    <span className="text-xs uppercase tracking-wider font-semibold text-brand-dark">
                                        {c.label}
                                    </span>
                                </>
                            );
                            return (
                                <li
                                    key={c.label}
                                    data-testid={`about-cert-${slug}`}
                                >
                                    {c.href ? (
                                        <a
                                            href={c.href}
                                            target="_blank"
                                            rel="noopener noreferrer nofollow"
                                            className="flex items-center gap-3 bg-white border border-brand-dark/10 px-5 py-4 hover:border-brand-cyan transition-colors"
                                        >
                                            {inner}
                                        </a>
                                    ) : (
                                        <div className="flex items-center gap-3 bg-white border border-brand-dark/10 px-5 py-4">
                                            {inner}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </Container>
            </Section>

            {/* CTA band */}
            <Section dark className="py-16 md:py-20" data-testid="about-cta-band">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                        <div className="lg:col-span-7">
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
                                Want to work with us?
                            </h2>
                            <p className="mt-3 text-base text-white/70 max-w-xl">
                                Free, no-obligation quote in 24 hours. Or call us
                                directly — we actually answer the phone.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                                <Link to={track.contact}>
                                    Start My Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton asChild variant="outline" tone="onDark" size="lg" className="w-full lg:w-auto">
                                <a href={COMPANY.phoneHref}>
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {COMPANY.phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
